import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './auth/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { CallbackPage } from './pages/CallbackPage';
import { BriefingPage } from './pages/BriefingPage';
import { DocsPage } from './pages/DocsPage';
import { DigestListPage } from './pages/DigestListPage';
import { DigestPage } from './pages/DigestPage';
import { TodayPage } from './pages/TodayPage';
import { AppShell } from './components/AppShell';
import { ResearchPage } from './pages/ResearchPage';
import { ResearchItemPage } from './pages/ResearchItemPage';
import { ResearchPosterPage } from './pages/ResearchPosterPage';
import { InputsPage } from './pages/InputsPage';
import { CapturePage } from './pages/CapturePage';
import { NotificationsPage } from './pages/NotificationsPage';
import { PodcastPage } from './pages/PodcastPage';
import { PodcastSetupPage } from './pages/PodcastSetupPage';
import { PublicResearchPage } from './pages/PublicResearchPage';
import { AssistantProvider } from './components/assistant/AssistantProvider';
import { MeProvider } from './api/MeContext';

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <div className="min-h-dvh bg-background text-foreground antialiased">
      <MeProvider>
        <AssistantProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<CallbackPage />} />
            {/* Anonymous share surface for visibility='public' research — NO auth. */}
            <Route path="/p/:slug" element={<PublicResearchPage />} />
            {/* Full-viewport poster lives OUTSIDE the app shell — no header chrome. */}
            <Route
              path="/research/:researchId/poster"
              element={
                <RequireAuth>
                  <ResearchPosterPage />
                </RequireAuth>
              }
            />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="/" element={<DigestListPage />} />
              <Route path="/today" element={<TodayPage />} />
              <Route path="/digests/:date" element={<DigestPage />} />
              <Route path="/briefing" element={<BriefingPage />} />
              {/* Same page with the package slide-over open — the panel's href
                is real, so middle-click / copy-link / direct open all land on
                the requested doc instead of the catch-all redirect. */}
              <Route path="/briefing/packages/:packageSlug" element={<BriefingPage />} />
              {/* Index redirects to the first doc once the listing loads. */}
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/docs/:docSlug" element={<DocsPage />} />
              <Route path="/research" element={<ResearchPage />} />
              <Route path="/research/:researchId" element={<ResearchItemPage />} />
              <Route path="/inputs" element={<InputsPage />} />
              <Route path="/capture" element={<CapturePage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/podcast" element={<PodcastPage />} />
              <Route path="/podcast/setup" element={<PodcastSetupPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AssistantProvider>
      </MeProvider>
    </div>
  );
}
